import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Pencil } from "lucide-react";
import ColorPaletteAdvisorV1 from "./ColorPaletteAdvisorV1";

import {
  approveColorPaletteV1,
  buildColorPaletteV1,
  getCurrentColorPaletteV1,
  refineColorPaletteV1,
  getTypographyPreferences,
} from "../../lib/brandStudioApi";
import {
  approximateCmyk,
  contrastRatio,
  rgbLabel,
} from "../../lib/colorSystemPresentation.mjs";
import {
  normalizePaletteHex, validPaletteHex, paletteDraftError, paletteNearWarnings,
} from "../../lib/colorPaletteRefinement.mjs";

const ROLE_LABELS = {
  primary_dark: "Основной тёмный",
  primary_light: "Основной светлый",
  support: "Поддерживающий",
  accent: "Акцентный",
  neutral: "Нейтральный",
};

const readableText = (hex) =>
  contrastRatio(hex, "#2A1D14") >= contrastRatio(hex, "#FFFDF8")
    ? "#2A1D14"
    : "#FFFDF8";

const errorText = (problem) => {
  const detail = problem?.response?.data?.detail;
  const messages = {
    palette_version_conflict: "Палитра уже изменилась. Обнови страницу перед новой правкой.",
    palette_duplicate_hex: "Этот HEX уже есть в палитре. Выбери другой оттенок.",
    palette_duplicate_name: "Такое название уже есть. Назови оттенки по-разному.",
    palette_color_missing: "Цвет больше недоступен. Обнови страницу.",
    palette_missing: "Палитра пока не готова. Обнови страницу.",
  };
  if (typeof detail === "object" && detail?.code)
    return messages[detail.code] || "Не удалось сохранить изменения. Попробуй ещё раз.";
  if (typeof detail === "string") return detail;
  return "Не удалось собрать палитру. Исходные данные сохранены.";
};

export default function ColorPaletteCardV1({ onContinue, editMode = false }) {
  const [palette, setPalette] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [approving, setApproving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [typography, setTypography] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus("loading");
      setError("");
      try {
        const current = await getCurrentColorPaletteV1();
        if (cancelled) return;

        if (current && (editMode || current.status === "approved" || current.creation_method === "manual")) {
          setPalette(current);
          setStatus("ready");
          return;
        }

        if (editMode) {
          setError("Палитра ещё не сохранена. Вернись в Мой бренд и продолжи создание.");
          setStatus("error");
          return;
        }
        const result = await buildColorPaletteV1();
        if (cancelled) return;

        if (!result?.palette) {
          setError(result?.reason || "Палитра пока не готова.");
          setStatus("error");
          return;
        }

        setPalette(result.palette);
        setStatus("ready");
      } catch (problem) {
        if (cancelled) return;
        setError(errorText(problem));
        setStatus("error");
      }
    };

    load();
    getTypographyPreferences().then((state) => { if (!cancelled) setTypography(state?.selected_system || null); }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editMode]);

  const colors = useMemo(() => palette?.colors || [], [palette]);
  const selected = colors.find((color) => color.stable_id === selectedId);
  const dirty = Boolean(selected && draft && (
    normalizePaletteHex(draft.hex) !== selected.hex || draft.name.trim() !== selected.name || draft.role !== selected.role
  ));
  const draftError = draft ? paletteDraftError(colors, selectedId, draft) : "";
  const previewColors = colors.map((color) => selectedId === color.stable_id && draft
    ? { ...color, hex: validPaletteHex(draft.hex) ? normalizePaletteHex(draft.hex) : color.hex,
      name: draft.name.trim() || color.name, role: draft.role,
      hex_source: normalizePaletteHex(draft.hex) !== color.hex && validPaletteHex(draft.hex) ? "user" : color.hex_source }
    : color);
  const warnings = paletteNearWarnings(previewColors);

  const chooseColor = (color) => {
    if (saving) return;
    if (dirty && color.stable_id !== selectedId) {
      setError("Сначала сохрани изменения или нажми «Отмена».");
      return;
    }
    setSelectedId(color.stable_id);
    setDraft({ hex: color.hex, name: color.name, role: color.role });
    setError("");
  };

  const cancel = () => { setSelectedId(null); setDraft(null); setError(""); };

  const save = async (event) => {
    event.preventDefault();
    if (!selected || !draft || draftError || !dirty || saving) return;
    setSaving(true); setError("");
    try {
      const saved = await refineColorPaletteV1({
        expected_version_number: palette.version_number,
        expected_source_fingerprint: palette.source_fingerprint,
        stable_id: selectedId, hex: normalizePaletteHex(draft.hex),
        name: draft.name.trim(), role: draft.role,
      });
      setPalette(saved); setSelectedId(null); setDraft(null);
    } catch (problem) { setError(errorText(problem)); }
    finally { setSaving(false); }
  };

  const applyAdvice = async (recommendation) => {
    if (saving || approving || draft) return;
    if (recommendation.expected_version_number !== palette.version_number || recommendation.expected_source_fingerprint !== palette.source_fingerprint) {
      setError("Палитра уже изменилась. Проверь сочетание цветов заново."); return;
    }
    const color = colors.find((item) => item.stable_id === recommendation.stable_id);
    if (!color || color.hex !== recommendation.old_hex) return;
    setSaving(true); setError("");
    try {
      const saved = await refineColorPaletteV1({
        expected_version_number: recommendation.expected_version_number,
        expected_source_fingerprint: recommendation.expected_source_fingerprint,
        stable_id: color.stable_id, hex: recommendation.hex, name: color.name, role: color.role,
      });
      setPalette(saved);
    } catch (problem) { setError(errorText(problem)); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!palette || palette.status === "approved" || approving || saving || draft) return;
    setApproving(true);
    setError("");
    try {
      setPalette(await approveColorPaletteV1());
    } catch (problem) {
      setError(errorText(problem));
    } finally {
      setApproving(false);
    }
  };

  if (status === "loading") {
    return (
      <section
        className="rounded-[30px] border border-[#E1D4C2] bg-[#FFFDF9] px-6 py-12 text-center md:px-10"
        data-testid="color-palette-card-v1-loading"
      >
        <Loader2 className="mx-auto animate-spin text-[#8A6D53]" size={22} />
        <h2 className="mt-4 font-serif text-[29px] text-[#2B211A]">
          Собираю твою цветовую палитру
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[13px] leading-6 text-[#806B55]">
          Смотрю на твои референсы, повторяющиеся оттенки и явные пожелания.
        </p>
      </section>
    );
  }

  if (status === "error" || !palette) {
    return (
      <section
        className="rounded-[30px] border border-[#E2CFC8] bg-[#FFF8F4] p-6 md:p-8"
        data-testid="color-palette-card-v1-error"
      >
        <h2 className="font-serif text-[29px] text-[#2B211A]">
          Палитра пока не готова
        </h2>
        <p className="mt-2 text-[13px] leading-6 text-[#6D5842]">{error}</p>
      </section>
    );
  }

  const approved = palette.status === "approved";

  return (
    <section
      className="overflow-hidden rounded-[32px] border border-[#DFD2C0] bg-[#F8F3EB]"
      data-testid="color-palette-card-v1"
    >
      <div className="px-4 pb-4 pt-5 sm:px-6 sm:pb-6 sm:pt-7 md:px-9 md:pb-8 md:pt-9">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A6D53]">
          ЦВЕТОВАЯ ПАЛИТРА
        </div>
        <h2 className="mt-2 font-serif text-[34px] leading-tight text-[#2B211A] md:text-[42px]">
          Вот твои цвета
        </h2>
        <p className="mt-3 max-w-2xl text-[13px] leading-6 text-[#655141]">
          {palette.creation_method === "manual"
            ? "В основе — твои референсы и пожелания. Уточнённые цвета отражают твой выбор."
            : "Я собрала их из твоих референсов, заметок и пожеланий."}
        </p>
      </div>

      <div
        className={`grid min-w-0 grid-cols-2 border-y border-black/10 ${colors.length === 4 ? "md:grid-cols-4" : colors.length === 6 ? "md:grid-cols-6" : "md:grid-cols-5"}`}
        data-testid="color-palette-swatches"
      >
        {previewColors.map((color) => {
          const textColor = readableText(color.hex);
          return (
            <article
              key={color.stable_id}
              className={`relative flex min-h-[158px] min-w-0 flex-col justify-between border-b border-black/10 p-3 sm:p-4 md:min-h-[420px] md:border-b-0 md:border-r md:p-5 md:last:border-r-0 ${selectedId === color.stable_id ? "ring-2 ring-inset ring-current" : ""}`}
              style={{ backgroundColor: color.hex, color: textColor }}
              data-testid={"color-palette-swatch-" + color.stable_id}
            >
              {draft && (
                <button type="button" className="absolute inset-0 z-10 rounded-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px]"
                  aria-label={`Редактировать ${color.name}`} aria-pressed={selectedId === color.stable_id}
                  disabled={saving} onClick={() => chooseColor(colors.find((item) => item.stable_id === color.stable_id))}
                  data-testid={`color-palette-select-${color.stable_id}`} />
              )}
              <div>
                <div className="text-[8px] uppercase tracking-[0.10em] opacity-75 md:text-[9px] md:tracking-[0.18em]">
                  {ROLE_LABELS[color.role]}
                </div>
                <h3 className="mt-1.5 max-w-[12rem] break-words font-serif text-[19px] leading-[1.05] md:mt-2 md:text-[28px]">
                  {color.name}
                </h3>
              </div>

              <div className="space-y-0.5 text-[10px] leading-[1.35] opacity-90 md:space-y-1.5 md:text-[10.5px] md:leading-4">
                <div className="font-medium tracking-[0.08em]">{color.hex}</div>
                <div>{rgbLabel(color.hex)}</div>
                <div>{approximateCmyk(color.hex)}</div>
                {color.hex_source === "user" && <div className="pt-1 text-[10px]">Выбрано тобой</div>}
              </div>
            </article>
          );
        })}
      </div>

      <div className="px-4 py-5 sm:px-6 sm:py-6 md:px-9 md:py-7">
        {!draft && <ColorPaletteAdvisorV1 key={palette.source_fingerprint} palette={palette} onApply={applyAdvice} busy={saving || approving} />}
        {typography && typography.palette_fingerprint !== palette.source_fingerprint && <p className="mt-4 text-[13px] leading-5 text-[#655141]" role="status" data-testid="palette-advisor-typography-review">
          Палитра изменилась. Шрифты версии {typography.version_number} сохранены без изменений. {approved ? "Перепроверь их сочетание с новыми цветами." : "Сначала утверди новую палитру, затем перепроверь сочетание со шрифтами."}
        </p>}
        {draft && selected && (
          <form onSubmit={save} className="mb-6 min-w-0 rounded-2xl border border-[#DFD2C0] bg-[#FFFDF9] p-5" data-testid="color-palette-editor">
            <h3 className="font-serif text-[24px] text-[#2B211A]">Редактирование цвета</h3>
            <p className="mt-1 text-[13px] leading-5 text-[#655141]">Выберите цветную плашку выше. Изменения затронут только её.</p>
            <div className="mt-4 grid min-w-0 gap-4 sm:grid-cols-2">
              <div className="flex min-w-0 items-end gap-3">
                <div className="h-12 w-12 shrink-0 rounded-lg border border-black/10" style={{ backgroundColor: selected.hex }} aria-label={`Текущий цвет ${selected.hex}`} />
                <label className="min-w-0 text-[13px] font-medium text-[#4D3A2C]">Выбрать новый цвет
                  <span className="relative mt-1 flex min-h-12 w-20 items-center justify-center gap-2 rounded-lg border border-[#CDBBA4] bg-white focus-within:ring-2 focus-within:ring-[#655141] focus-within:ring-offset-2">
                    <span className="h-8 w-8 rounded border border-black/10" style={{ backgroundColor: validPaletteHex(draft.hex) ? normalizePaletteHex(draft.hex) : selected.hex }} aria-hidden="true" />
                    <Pencil size={16} aria-hidden="true" />
                  <input type="color" aria-label="Выбрать новый цвет" value={validPaletteHex(draft.hex) ? normalizePaletteHex(draft.hex) : selected.hex}
                    disabled={saving} onChange={(event) => setDraft({ ...draft, hex: event.target.value.toUpperCase() })}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-wait" data-testid="color-palette-picker" />
                  </span>
                </label>
              </div>
              <label className="text-[13px] font-medium text-[#4D3A2C]" htmlFor="palette-edit-hex">HEX
                <input id="palette-edit-hex" value={draft.hex} maxLength={7} spellCheck={false} autoComplete="off"
                  disabled={saving} onChange={(event) => setDraft({ ...draft, hex: event.target.value })}
                  className="mt-1 block min-h-12 w-full min-w-0 rounded-xl border border-[#DFD2C0] bg-white px-3 text-[14px]" data-testid="color-palette-hex" />
              </label>
              <label className="text-[13px] font-medium text-[#4D3A2C]" htmlFor="palette-edit-name">Название
                <input id="palette-edit-name" value={draft.name} maxLength={120} disabled={saving}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  className="mt-1 block min-h-12 w-full min-w-0 rounded-xl border border-[#DFD2C0] bg-white px-3 text-[14px]" data-testid="color-palette-name" />
              </label>
              <label className="text-[13px] font-medium text-[#4D3A2C]" htmlFor="palette-edit-role">Роль
                <select id="palette-edit-role" value={draft.role} disabled={saving}
                  onChange={(event) => setDraft({ ...draft, role: event.target.value })}
                  className="mt-1 block min-h-12 w-full min-w-0 rounded-xl border border-[#DFD2C0] bg-white px-3 text-[14px]" data-testid="color-palette-role">
                  {Object.entries(ROLE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>
            {draftError && <p className="mt-3 text-[12px] text-[#8B3F35]" role="alert">{draftError}</p>}
            <div className="mt-4 flex flex-wrap gap-3">
              <button type="submit" disabled={saving || !dirty || Boolean(draftError)} className="min-h-12 rounded-full bg-[#3D2A20] px-5 text-[13px] text-[#FFF8ED] disabled:opacity-50" data-testid="color-palette-save">{saving ? "Сохраняю…" : "Сохранить изменения"}</button>
              <button type="button" onClick={cancel} disabled={saving} className="min-h-12 rounded-full border border-[#CDBBA4] px-5 text-[13px] text-[#655141]" data-testid="color-palette-cancel">Отмена</button>
            </div>
          </form>
        )}
        {warnings.map((warning) => <p key={warning} role="status" className="mb-3 text-[12px] leading-5 text-[#806B55]" data-testid="color-palette-near-warning">{warning}</p>)}
        <p className="max-w-2xl text-[11px] leading-5 text-[#806B55]">
          CMYK указан как ориентир для печати. Финальный печатный цвет зависит
          от профиля типографии, бумаги и способа производства.
        </p>

        <div className="flex flex-wrap items-center gap-3">
        {!draft && approved && onContinue && (!editMode || (typography && (typography.palette_fingerprint !== palette.source_fingerprint || typography.palette_version !== palette.version_number))) && <button type="button" onClick={onContinue}
          disabled={saving || approving} className="mt-5 min-h-12 rounded-full bg-[#3D2A20] px-5 text-[13px] text-[#FFF8ED]" data-testid="color-palette-continue-typography">{editMode ? "Проверить шрифты" : "Продолжить к шрифтам"}</button>}
        {!draft && <button type="button" onClick={() => chooseColor(colors[0])} disabled={approving || saving}
          className="mt-5 min-h-12 rounded-full border border-[#CDBBA4] px-5 text-[13px] text-[#655141] disabled:opacity-50" data-testid="color-palette-refine">Отредактировать цвета</button>}
        {!draft && (approved ? (
          <div
            className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#EEF0DF] px-5 text-[13px] text-[#586044]"
            data-testid="color-palette-approved"
          >
            <Check size={15} />
            Да, это мои цвета
          </div>
        ) : (
          <button
            type="button"
            onClick={approve}
            disabled={approving || saving}
            className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#3D2A20] px-6 text-[13px] text-[#FFF8ED] disabled:opacity-50"
            data-testid="color-palette-approve"
          >
            {approving && <Loader2 size={15} className="animate-spin" />}
            {approving ? "Сохраняю…" : "Да, это мои цвета"}
          </button>
        ))}
        </div>

        {error && (
          <p className="mt-3 text-[12px] text-[#8B3F35]" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

